import React, { useEffect, useState } from "react";
import {
  Box,
  InputAdornment,
  Pagination,
  PaginationItem,
  Paper,
  TextField,
  Typography,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import Select from "react-select";

import CustomHeader from "../../../core/components/common/custom-header.component";
import CustomButton from "../../../core/components/ui/CustomButton";
import UserListComponent from "../components/user-list.component";
import { useUser } from "../hooks/useUser";
import theme from "../../../core/components/layout/theme";
import CloseIcon from "@mui/icons-material/Close";
import { RBACUIGuard } from "../../RBACPlugin";
import { permissions } from "../../../core/components/common/variables/static_variables";
import { useOrg } from "../../OrganizationPlugin/hooks/userOrg";
import { useNavigate } from "react-router-dom";

const customStyles = {
  control: (base, state) => ({
    ...base,
    height: 42,
    width: 250,
    fontSize: 14,
    backgroundColor: "transparent",
    borderColor: state.isFocused
      ? theme.palette.primary.main
      : "#dce8f5",
    boxShadow: state.isFocused
      ? `0 0 0 1px ${theme.palette.primary.main}`
      : "none",
    "&:hover": {
      borderColor: theme.palette.primary.main,
    },
  }),
  placeholder: (base) => ({
    ...base,
    fontSize: 12,
  }),
  menu: (provided) => ({
    ...provided,
    zIndex: 99999,
    fontSize: 14,
  }),
};

const searchOptions = [
  { value: "email", label: "Email" },
  { value: "firstname", label: "First Name" },
  { value: "lastname", label: "Last Name" },
];

export default function UserListPage() {
  const rbacStorage = JSON.parse(localStorage.getItem("rbac-storage"));
  const org_uid = rbacStorage?.state?.currentProfile?.orgId || null;
  const view_org_uid = new URLSearchParams(window.location.search).get("org_uid");

  const organizationId = view_org_uid || org_uid;

  const { listUser, userList, loading, listUserFilter, userFilter, pagination, isCreatingUser } = useUser();

  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(0);
  const [searchField, setSearchField] = useState(null);
  const [searchValue, setSearchValue] = useState("");
  const [isFiltering, setIsFiltering] = useState(false);
  const navigate = useNavigate()

  const rowsPerPage = 10;

  const handleFieldChange = (selectedOption) => {
    setSearchField(selectedOption);
    setSearchValue("");
    setCurrentPage(1);
  };

  const handleSearch = () => {
    if (searchField && searchValue) {
      setIsFiltering(true);
      setCurrentPage(1);
      listUserFilter({
        searchFor: searchField.value,
        searchVal: encodeURIComponent(searchValue),
        page: 1,
        limit: rowsPerPage,
        org_uid: organizationId,
      });
    }
  };

  const handlePaginationChange = (event, page) => {
    setCurrentPage(page);
  };

  useEffect(() => {
    if(organizationId){
      console.log("Fetching users for org:", organizationId);
    }
  }, [organizationId]);

  useEffect(() => {

    const searchFor = searchField && searchField.value ? searchField.value : '';
    const searchVal = searchValue || '';

    if (isFiltering && searchFor && searchVal) {
      listUserFilter({
        page: currentPage,
        limit: rowsPerPage,
        searchFor,
        searchVal,
        org_uid: organizationId,
      });
    } else {
      listUser(organizationId, currentPage, searchFor, searchVal);
    }
  }, [
    currentPage,
    rowsPerPage,
    isFiltering,
    organizationId,
    searchField, // Watch full object to capture .value changes
    searchValue,
  ]);

  const handleClearSearch = () => {
    setSearchValue("");
    setSearchField(null);
    setIsFiltering(false);
    setCurrentPage(0);
    listUser({organizationId,  page: 1});
  };

  let create_user = '/user/create';

  if (!org_uid && view_org_uid) {
    create_user = `/organization/user/create?org_uid=${view_org_uid}`;
  }

  useEffect(() => {
    if(isCreatingUser){
      listUser(organizationId, currentPage);
    }
  },[isCreatingUser])

  return (
    <>
      <Box sx={{ textAlign: "start" }}>
        <CustomHeader headerText="User Management" />
      </Box>

      <Paper
        elevation={0}
        sx={{
          width: "100%",
          p: 5,
          mt: 3,
          borderRadius: 1,
          border: "1px solid #e5edf6",
          boxShadow: "none",
          overflow: "hidden",
        }}
      >
        {/* Header Section */}
        <Box sx={{ display: "flex", alignItems: "center", mb: 2 }} gap={1}>
          <Typography sx={{ fontSize: 18, fontWeight: "bold" }}>
            All Users
          </Typography>
          
          <Box sx={{ flexGrow: 1 }} />

          
          {/* Filter and Add Button */}
          <Box sx={{ display: "flex", alignItems: "center" }}>
            <RBACUIGuard requiredPermission={permissions.users.FILTER_LIST_ACCOUNT}>
              <Select
                styles={customStyles}
                options={searchOptions}
                value={searchField}
                onChange={handleFieldChange}
                placeholder="Select filter"
              />
              &nbsp;&nbsp;
              {searchField && (
                <TextField
                  placeholder={searchField.label}
                  value={searchValue}
                  onChange={(e) => setSearchValue(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter") handleSearch();
                  }}
                  variant="outlined"
                  autoComplete="off"
                  sx={{
                    "& .MuiInputBase-root": {
                      height: 42,
                      width: 300,
                      backgroundColor: "transparent",
                      mr: 1,
                    },
                  }}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <Typography sx={{ fontSize: 12 }}>Search:</Typography>
                      </InputAdornment>
                    ),
                    endAdornment: searchValue && (
                      <InputAdornment
                        position="end"
                        sx={{ cursor: "pointer" }}
                        onClick={handleClearSearch}
                      >
                        <CloseIcon fontSize="small" />
                      </InputAdornment>
                    ),
                    style: {
                      padding: "10px",
                      fontSize: 14,
                      fontStyle: "italic",
                    },
                  }}
                />
              )}
            </RBACUIGuard>
            
            
            &nbsp;&nbsp;
            <RBACUIGuard requiredPermission={permissions.users.CREATE_ACCOUNT}>
              <CustomButton
                variant="contained"
                startIcon={<AddIcon fontSize="small" />}
                label="Add User"
                customStyles={{ fontSize: 14 }}
                onClick={() => navigate(create_user)}
              />
            </RBACUIGuard>
              
          </Box>
        
        </Box>

        {/* User List */}
        <UserListComponent
          userList={isFiltering ? userFilter : userList}
          loading={loading}
        />

        {!loading && (
          <Box textAlign="center" mt={3} display="flex" justifyContent="center" gap={2}>
            <CustomButton
              label="Back"
              variant="outlined"
              disabled={currentPage <= 1}
              onClick={() => setCurrentPage((prev) => prev - 1)}
              customStyles={{ fontSize: 14, px: 4 }}
            />
            <CustomButton
              label="Load More"
              variant="outlined"
              disabled={
                (isFiltering ? userFilter?.length : userList?.length) < rowsPerPage
              }
              onClick={() => setCurrentPage((prev) => prev + 1)}
              customStyles={{ fontSize: 14, px: 4 }}
            />
          </Box>
        )}



      </Paper>
    </>
  );
}
